import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Headset, Mail, Phone, MessageCircle, Loader2, UserX } from 'lucide-react';
import { motion } from 'motion/react';
import { GlassCard } from '@/components/glass';
import { useSupportContacts } from '../hooks/useSupportContacts';
import { useCreateConversation } from '@/features/messaging/hooks/useMessaging';
import { setPendingPush } from '@/features/messaging/messagingNavBridge';
import { useAuthStore } from '@/features/auth';
import { staggerContainer, staggerItem } from '@/features/admin/utils/adminAnimations';

export function ContactSupportPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading } = useSupportContacts();
  const createConversation = useCreateConversation();
  const currentUserId = useAuthStore((s) => s.user?.id);

  const handleMessage = async (userId: string) => {
    try {
      const result = await createConversation.mutateAsync({
        type: 'dm',
        participantIds: [userId],
      });
      setPendingPush('ChatActivity', { conversationId: result.conversation.id });
      navigate(`/messages/${result.conversation.id}`);
    } catch {
      // mutation error handled by react-query
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
      className="mx-auto max-w-2xl space-y-6 px-4 py-6"
    >
      {/* Header */}
      <motion.div variants={staggerItem} className="space-y-2 text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-500/15">
          <Headset className="h-7 w-7 text-primary-600" />
        </div>
        <h1 className="text-2xl font-bold text-neutral-900">{t('support.title')}</h1>
      </motion.div>

      {/* Contact Info */}
      {(data?.contactEmail || data?.contactPhone) && (
        <motion.div variants={staggerItem}>
          <GlassCard title={t('support.contactInfo')} icon={<Mail className="h-4 w-4" />}>
            <div className="grid gap-3 sm:grid-cols-2">
              {data.contactEmail && (
                <a
                  href={`mailto:${data.contactEmail}`}
                  className="flex items-center gap-3 rounded-xl border border-gray-100 bg-gray-50/50 px-4 py-3 transition-colors hover:bg-gray-100/80"
                >
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-500/15">
                    <Mail className="h-4 w-4 text-blue-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-neutral-500">{t('settings.contactEmail')}</p>
                    <p className="truncate text-sm font-semibold text-neutral-800">{data.contactEmail}</p>
                  </div>
                </a>
              )}
              {data.contactPhone && (
                <a
                  href={`tel:${data.contactPhone}`}
                  className="flex items-center gap-3 rounded-xl border border-gray-100 bg-gray-50/50 px-4 py-3 transition-colors hover:bg-gray-100/80"
                >
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-green-500/15">
                    <Phone className="h-4 w-4 text-green-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-neutral-500">{t('settings.contactPhone')}</p>
                    <p className="truncate text-sm font-semibold text-neutral-800">{data.contactPhone}</p>
                  </div>
                </a>
              )}
            </div>
          </GlassCard>
        </motion.div>
      )}

      {/* Support Team */}
      <motion.div variants={staggerItem}>
        <GlassCard title={t('support.supportTeam')} icon={<Headset className="h-4 w-4" />}>
          {data?.users && data.users.length > 0 ? (
            <div className="space-y-2">
              {data.users.map((user) => (
                <div
                  key={user.id}
                  className="flex items-center gap-3 rounded-xl border border-gray-100 bg-gray-50/50 px-4 py-3"
                >
                  {user.avatarUrl ? (
                    <img
                      src={user.avatarUrl}
                      alt=""
                      className="h-10 w-10 shrink-0 rounded-full object-cover"
                    />
                  ) : (
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-500/15 text-sm font-bold text-primary-700">
                      {user.firstName?.[0]}{user.lastName?.[0]}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-neutral-800">
                      {user.firstName} {user.lastName}
                    </p>
                  </div>
                  {user.id === currentUserId ? (
                    <span className="inline-flex items-center gap-1.5 rounded-lg bg-gray-300 px-3 py-1.5 text-xs font-bold text-gray-500 cursor-not-allowed">
                      <MessageCircle className="h-3.5 w-3.5" />
                      {t('support.messageUser')}
                    </span>
                  ) : (
                    <button
                      onClick={() => handleMessage(user.id)}
                      disabled={createConversation.isPending}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-xs font-bold text-white transition-colors hover:bg-primary-700 disabled:opacity-50"
                    >
                      <MessageCircle className="h-3.5 w-3.5" />
                      {t('support.messageUser')}
                    </button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2 py-8 text-center text-neutral-400">
              <UserX className="h-8 w-8" />
              <p className="text-sm">{t('support.noSupportUsers')}</p>
            </div>
          )}
        </GlassCard>
      </motion.div>
    </motion.div>
  );
}
